import React, { useState, useEffect, useRef, useMemo } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Eye } from "lucide-react";
import styles from "./NotificationsImproved.module.scss";
import hubTabStyles from "../../notificationsHub/NotificationHubList.module.scss";
import useNavVisibility from "../../hooks/useNavVisibility.ts";
import { setViewNotification } from "../../store/actions";
import type { ApiNotification } from "../../notificationsHub/notificationEntityShape.ts";
import type { NotificationHubItem } from "../../notificationsHub/mapApiNotificationToHubItem.ts";
import { mapApiNotificationsList } from "../../notificationsHub/mapApiNotificationToHubItem.ts";
import { formatRelativeHubDate } from "../../notificationsHub/formatHubDate.ts";
import {
  countsByHubTab,
  filterHubItemsByTab,
  hubTabsForItems,
  type HubFilterTab,
} from "../../notificationsHub/notificationHubFilters.ts";
import { HUB_NOTIFICATION_TYPES_VISIBLE_ONLY_TO_ADMIN } from "../../notificationsHub/notificationTypes.ts";

type UserDataShape = { rols?: { name?: string }[] };

interface NotificationsImprovedProps {
  newNotifications: number;
  solicitudesList: any[];
  userData?: UserDataShape;
  isMobile?: boolean;
  onClose?: () => void;
}

const NotificationsImproved: React.FC<NotificationsImprovedProps> = ({
  newNotifications,
  solicitudesList,
  userData,
  isMobile = false,
  onClose,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filter, setFilter] = useState<HubFilterTab>("all");
  const [visibleCount, setVisibleCount] = useState(5);
  const [localNotifications, setLocalNotifications] = useState<any[]>([]);
  const [markingInProgress, setMarkingInProgress] = useState<Set<string>>(new Set());
  const [isSyncing, setIsSyncing] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filterTabsRef = useRef<HTMLDivElement>(null);
  const { ocultarEnMobile } = useNavVisibility();

  const isAdmin = Boolean(userData?.rols?.some((r) => r.name === "ADMIN"));

  useEffect(() => {
    if (solicitudesList && markingInProgress.size === 0 && !isSyncing) {
      setLocalNotifications(solicitudesList);
    }
  }, [solicitudesList, markingInProgress.size, isSyncing]);

  const mappedItems = useMemo(() => {
    const list = Array.isArray(localNotifications) ? localNotifications : [];
    const mapped = mapApiNotificationsList(list as ApiNotification[]);
    if (isAdmin) return mapped;
    return mapped.filter(
      (i) => !HUB_NOTIFICATION_TYPES_VISIBLE_ONLY_TO_ADMIN.has(i.type)
    );
  }, [localNotifications, isAdmin]);

  const localUnreadCount = useMemo(
    () => mappedItems.filter((i) => !i.read).length,
    [mappedItems]
  );

  const hasAccountCategory = useMemo(
    () => mappedItems.some((i) => i.category === "account"),
    [mappedItems]
  );
  const hasSystemCategory = useMemo(
    () => mappedItems.some((i) => i.category === "system"),
    [mappedItems]
  );

  const tabLabels = useMemo(
    () => hubTabsForItems(hasAccountCategory, hasSystemCategory),
    [hasAccountCategory, hasSystemCategory]
  );

  useEffect(() => {
    if (filter === "account" && !hasAccountCategory) setFilter("all");
    if (filter === "system" && !hasSystemCategory) setFilter("all");
  }, [filter, hasAccountCategory, hasSystemCategory]);

  useEffect(() => {
    setVisibleCount(5);
  }, [filter]);

  const countsByTab = useMemo(() => countsByHubTab(mappedItems), [mappedItems]);

  const filteredNotifications = useMemo(() => {
    const list = filterHubItemsByTab(mappedItems, filter);
    list.sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });
    return list;
  }, [mappedItems, filter]);

  const visibleNotifications = filteredNotifications.slice(0, visibleCount);
  const hasMore = filteredNotifications.length > visibleCount;

  const handleNotificationClick = () => {
    if (isMobile || ocultarEnMobile) {
      navigate("/notificaciones");
      onClose?.();
    } else {
      setShowDropdown(!showDropdown);
    }
  };

  const handleViewAll = () => {
    navigate("/notificaciones");
    setShowDropdown(false);
    onClose?.();
  };

  const handleMarkAllRead = () => {
    const unreadNotifications = localNotifications.filter((n) => n.view !== true);

    setLocalNotifications((prev) => prev.map((n) => ({ ...n, view: true })));

    const allIds = unreadNotifications.map((n) => String(n.id));
    setMarkingInProgress(new Set(allIds));

    unreadNotifications.forEach((notification) => {
      dispatch(setViewNotification(notification.id) as any);
    });

    setTimeout(() => {
      setMarkingInProgress(new Set());

      setTimeout(() => {
        setIsSyncing(true);
        if (solicitudesList) {
          setLocalNotifications(solicitudesList);
        }
        setTimeout(() => setIsSyncing(false), 100);
      }, 1000);
    }, 500);
  };

  const handleMarkAsRead = (notificationId: string) => {
    if (markingInProgress.has(notificationId)) return;

    setLocalNotifications((prev) =>
      prev.map((n) =>
        String(n.id) === String(notificationId) ? { ...n, view: true } : n
      )
    );
    setMarkingInProgress((prev) => new Set(Array.from(prev).concat(notificationId)));

    dispatch(setViewNotification(notificationId) as any);

    setTimeout(() => {
      setMarkingInProgress((prev) => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });

      setTimeout(() => {
        setIsSyncing(true);
        if (solicitudesList) {
          setLocalNotifications(solicitudesList);
        }
        setTimeout(() => setIsSyncing(false), 100);
      }, 1000);
    }, 500);
  };

  const handleHubItemActivate = (item: NotificationHubItem) => {
    const sid = String(item.id);
    if (markingInProgress.has(sid)) return;
    if (!item.read && item.id) {
      handleMarkAsRead(sid);
    }
    navigate(item.deeplink || "/notificaciones");
    setShowDropdown(false);
    onClose?.();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (filterTabsRef.current && showDropdown) {
        e.preventDefault();
        e.stopPropagation();
        const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
        filterTabsRef.current.scrollLeft += delta;
      }
    };

    if (showDropdown) {
      const filterTabs = filterTabsRef.current;
      if (filterTabs) {
        filterTabs.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
          filterTabs.removeEventListener('wheel', handleWheel);
        };
      }
    }
  }, [showDropdown]);

  useEffect(() => {
    if (filterTabsRef.current && showDropdown) {
      const activeTab = filterTabsRef.current.querySelector(`[data-tab-id="${filter}"]`) as HTMLElement;
      if (activeTab) {
        const container = filterTabsRef.current;
        const containerRect = container.getBoundingClientRect();
        const tabRect = activeTab.getBoundingClientRect();
        
        const tabCenter = tabRect.left + tabRect.width / 2;
        const containerCenter = containerRect.left + containerRect.width / 2;
        const scrollOffset = tabCenter - containerCenter;
        
        container.scrollTo({
          left: container.scrollLeft + scrollOffset,
          behavior: 'smooth'
        });
      }
    }
  }, [filter, showDropdown]);

  const renderDropdown = () => {
    if (isMobile || ocultarEnMobile || !showDropdown) return null;

    return (
      <div ref={dropdownRef} className={styles.notificationsDropdown}>
        <div className={styles.notificationsHeader}>
              <div className={styles.headerTitle}>
                <Bell size={20} />
                <h3>Notificaciones</h3>
                {localUnreadCount > 0 && (
                  <span className={styles.unreadCount}>{localUnreadCount}</span>
                )}
              </div>
          <div className={styles.headerActions}>
            <button 
              className={styles.actionButton}
              onClick={handleMarkAllRead}
              title="Marcar todas como leídas"
            >
              <Check size={16} />
            </button>
            <button
              className={styles.actionButton}
              onClick={handleViewAll}
              title="Centro de notificaciones"
            >
              <Eye size={16} />
            </button>
          </div>
        </div>

        <div className={styles.filterTabsWrapper}>
          <div
            ref={filterTabsRef}
            className={styles.dropdownTabRow}
          >
            {tabLabels.map((tab) => {
              const n = countsByTab[tab.id];
              const label = n > 0 ? `${tab.label} (${n})` : tab.label;
              return (
                <button
                  key={tab.id}
                  data-tab-id={tab.id}
                  type="button"
                  className={`${hubTabStyles.tab} ${filter === tab.id ? hubTabStyles.tabActive : ""}`}
                  onClick={() => setFilter(tab.id)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.notificationsContent}>
          {visibleNotifications.length > 0 ? (
            <>
                  <div className={styles.notificationsList}>
                    {visibleNotifications.map((item) => (
                      <div
                        key={item.id || item.type + (item.createdAt ?? "")}
                        className={`${styles.notificationItem} ${!item.read ? styles.unread : ""} ${markingInProgress.has(String(item.id)) ? styles.marking : ""}`}
                        onClick={() => handleHubItemActivate(item)}
                        style={{ cursor: "pointer" }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleHubItemActivate(item);
                          }
                        }}
                      >
                        <div className={styles.notificationContent}>
                          <p className={styles.hubPreviewTitle}>{item.title}</p>
                          <p className={styles.hubPreviewBody}>{item.body}</p>
                          <small className={styles.notificationDate}>
                            {formatRelativeHubDate(item.createdAt)}
                          </small>
                          <div className={styles.markAsReadHint}>
                            Clic para abrir
                            {item.deeplink ? " · " + item.actionLabel : ""}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
              
              {hasMore && (
                <div className={styles.loadMore}>
                  <button 
                    className={styles.loadMoreButton}
                    onClick={() => setVisibleCount(prev => prev + 5)}
                  >
                    Ver más ({filteredNotifications.length - visibleCount} restantes)
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <Bell size={48} />
              <p>No hay notificaciones en este filtro.</p>
              <small>Probá otra pestaña o abrí el centro de notificaciones.</small>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.notificationsContainer}>
      <button
        className={`${styles.notificationButton} ${showDropdown ? styles.active : ''}`}
        onClick={handleNotificationClick}
        title="Notificaciones"
      >
            <div className={styles.notificationIcon}>
              <Bell size={20} />
              {localUnreadCount > 0 && (
                <span className={styles.notificationBadge}>
                  {localUnreadCount > 99 ? '99+' : localUnreadCount}
                </span>
              )}
            </div>
      </button>
      
      {renderDropdown()}
    </div>
  );
};

const mapStateToProps = (state: {
  dashboard?: { newNotifications?: number; solicitudesList?: unknown[] };
  general?: { userData?: UserDataShape };
}) => ({
  newNotifications: state.dashboard?.newNotifications,
  solicitudesList: state.dashboard?.solicitudesList,
  userData: state.general?.userData,
});

export default connect(mapStateToProps)(NotificationsImproved);
